import { useHistory } from 'react-router-dom';
import { useAccountAttestation } from '../../../accounts/attestation/useAccountAttestation';
import { determineRenewalRoute } from '../../../accounts/accountUtils';
import useRenewQuoteFeature from '../../../console/workflows/renew-quote/useRenewQuoteFeature';
import useNotifications from '../../../_lib/notifications/useNotifications';
import { useCheckP250RenewalEligibility } from './useCheckP250RenewalEligibility';

export const CANNOT_RENEW_NOW =
  'Policy cannot be renewed at this time. Please contact support@cowbellcyber.ai';

/** What the account's quote status answers, as far as the renewal flow reads it. */
export interface AccountQuoteStatus {
  readonly productType?: string;
  readonly policyId?: string;
  /** How far the policy is from its expiration, and the window a renewal quote opens in. */
  readonly beforeDays?: number;
  readonly withinDays?: number;
}

/** The policy a renewal quote is generated for. */
export interface RenewalQuotePolicy {
  readonly product: string | undefined;
  readonly policyId: string | undefined;
  readonly accountId: string | null | undefined;
}

interface GenerateRenewalQuoteOptions {
  /**
   * What is shown when the account's status says the renewal cannot be quoted yet. The attestation hook's own notice
   * where absent.
   */
  readonly onUnavailable?: (data: AccountQuoteStatus) => void;
}

/**
 * Generate Renewal Quote, as the agency policy menus run it: the renewal product check, then the account's quote status,
 * then the renewal flow's route (`determineRenewalRoute`), with an error where the account goes to new business or no
 * route is known.
 */
export function useGenerateRenewalQuote({
  onUnavailable,
}: GenerateRenewalQuoteOptions = {}): (
  policy: RenewalQuotePolicy
) => Promise<void> {
  const { push } = useHistory();
  const notifications = useNotifications();
  const renewalsFeature = useRenewQuoteFeature();
  const checkEligibility = useCheckP250RenewalEligibility();

  const { handleCheckStatusOnAccount } = useAccountAttestation({
    onNewBusinessRedirect: () => notifications.error(CANNOT_RENEW_NOW),
    onRenewalRedirect: (
      apiData: AccountQuoteStatus,
      accountId: string | null | undefined
    ) => {
      const route = determineRenewalRoute(
        { ...apiData, accountId },
        renewalsFeature.on
      );
      if (route === null) {
        notifications.error(CANNOT_RENEW_NOW);
      } else {
        push(route);
      }
    },
    ...(onUnavailable ? { onUnavailableRedirect: onUnavailable } : {}),
  });

  return async ({ product, policyId, accountId }) => {
    const isEligible = await checkEligibility({ product, policyId });
    if (!isEligible) {
      return;
    }
    handleCheckStatusOnAccount(undefined, accountId);
  };
}
